import React from "react";

import { LockIcon } from "@packages/ds";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { OrganizationProjectStatusesTypes } from "Api/organizations/projects/OrganizationProjects.types";
import Tag from "ds/Tag";
import { getOrganizationId } from "Libs/utils";
import { useShowHipaa } from "Reducers/app/hooks/useShowHipaa";
import { selectFeatureUsageBasedBillingEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import { useTrialInfo } from "Reducers/organization/hooks/useTrialnfo";
import { subscriptionSelector } from "Reducers/subscription";
import PlaceholderText from "src/organization/common/components/PlaceholderText";
import { useAppSelector } from "Store/hooks";

import { OrganizationProjectMoreLinks } from "../OrganizationProjectMoreLinks";
import { ProvisioningProjectCard } from "../ProjectProvisioning";
import { greenLeafColor, useProjectRegion } from "../useProjectRegion";

import * as S from "./ProjectListItem.styles";

import type { OrganizationProject } from "Api/organizations/projects/OrganizationProjects.types";
type Props = {
  hasProjectAccess: boolean;
  organizationLabel: string;
  organizationName: string;
  projectId: string;
  projectPlan: string;
  projectRegion: string;
  projectSubscriptionId: string;
  projectStatus: string;
  projectTitle: string;
  projectType: string;
  shouldShowProjectOwner: boolean;
  regionSuffix?: string;
  regionTitle?: string;
  projectCreatedAt: string;
  refetchOrganizationProjectList: () => void;
  project: OrganizationProject;
};

const ProjectListItem = ({
  hasProjectAccess,
  organizationLabel,
  organizationName,
  projectId,
  projectPlan,
  projectRegion,
  projectSubscriptionId,
  projectStatus,
  projectTitle,
  projectType,
  regionSuffix,
  regionTitle,
  shouldShowProjectOwner,
  projectCreatedAt,
  refetchOrganizationProjectList,
  project
}: Props) => {
  const usageBasedPagesEnabled = useAppSelector(
    selectFeatureUsageBasedBillingEnabled
  );
  const { isGreen } = useProjectRegion(projectRegion);

  const Container = hasProjectAccess ? Link : "div";
  const intl = useIntl();
  const { config, isTrial } = useTrialInfo();
  const { tagText, tagVariant = "rose" } = config;

  const subscription = useAppSelector(state => {
    return subscriptionSelector(state, {
      organizationId: organizationName,
      projectId,
      id: projectSubscriptionId
    });
  });
  const organizationId = useAppSelector(state =>
    getOrganizationId(state, organizationName)
  );

  const showHipaa = useShowHipaa({
    projectId,
    subscription
  });
  const projectIsSuspended =
    hasProjectAccess &&
    projectStatus === OrganizationProjectStatusesTypes.suspended;
  const projectIsProvisioning =
    projectStatus === OrganizationProjectStatusesTypes.requested;
  const projectIsInProvisioningFailure =
    projectStatus === OrganizationProjectStatusesTypes.failed;

  if (projectIsProvisioning || projectIsInProvisioningFailure) {
    return (
      <ProvisioningProjectCard
        isListView
        organizationId={organizationId!}
        projectId={projectId}
        projectName={projectTitle}
        refetchOrganizationProjectList={refetchOrganizationProjectList}
        projectIsInProvisioningFailure={projectIsInProvisioningFailure}
        projectCreatedAt={projectCreatedAt}
      />
    );
  }

  return (
    <S.Wrapper>
      <Container
        to={`/${organizationName}/${projectId}`}
        id={`project-card-${projectId}`}
      >
        <div
          className="row"
          style={
            hasProjectAccess ? { cursor: "pointer" } : { cursor: "not-allowed" }
          }
        >
          <S.ProjectName className="cell cell-project">
            {projectTitle || <PlaceholderText text="loading" />}

            {projectType === "dedicated" && (
              <Tag>
                <FormattedMessage id="dedicated" />
              </Tag>
            )}

            {projectIsSuspended ? (
              <Tag variant="red">
                {intl.formatMessage({
                  id: "project.card.tag.suspended",
                  defaultMessage: "Suspended"
                })}
              </Tag>
            ) : showHipaa ? (
              <Tag variant="blue">
                {intl.formatMessage({
                  id: "hipaa",
                  defaultMessage: "HIPAA"
                })}
              </Tag>
            ) : (
              isTrial && (
                <Tag variant={tagVariant}>
                  {intl.formatMessage({
                    id: `trial_state.${tagText}`,
                    defaultMessage: "Free trial"
                  })}
                </Tag>
              )
            )}

            {!hasProjectAccess && (
              <S.NoAccessTag>
                <S.IconWrapper>
                  <LockIcon color="var(--mode-vector-neutral-strong" />
                </S.IconWrapper>
                <FormattedMessage id="project.card.tag.no-access" />
              </S.NoAccessTag>
            )}
          </S.ProjectName>

          {shouldShowProjectOwner && (
            <S.Ellipsis className="cell cell-organization col-2">
              {organizationLabel}
            </S.Ellipsis>
          )}
          <div className="cell cell-region col-2">
            {regionTitle || regionSuffix ? (
              <S.RegionWrapper>
                {isGreen && (
                  <S.Icon>
                    <S.RegionLeaf
                      fill={`var(${greenLeafColor})`}
                      width={24}
                      height={24}
                    />
                  </S.Icon>
                )}{" "}
                <span className="region-label">
                  {regionTitle}
                  <S.RegionSuffix $inline>{regionSuffix}</S.RegionSuffix>
                </span>
              </S.RegionWrapper>
            ) : (
              projectRegion
            )}
          </div>
          {!usageBasedPagesEnabled && (
            <div className="cell cell-plan col-2">{projectPlan}</div>
          )}
          <S.ProjectIdCell>{projectId}</S.ProjectIdCell>
        </div>
      </Container>
      <S.ProjectActions>
        <OrganizationProjectMoreLinks
          projectId={projectId}
          projectSubscriptionId={projectSubscriptionId}
          organizationName={organizationName}
          project={project}
          hasProjectAccess={hasProjectAccess}
        />
      </S.ProjectActions>
    </S.Wrapper>
  );
};

export default ProjectListItem;
